// Audit ledger capacity view model. Kept free of browser APIs so the alert thresholds are unit tested.

/** Audit ledger occupancy. Receipts have a hard cap: governed actions fail closed when it is reached. */
export interface AuditCapacity {
  recordCount: number;
  retainedRecords: number;
  recordCap: number;
  receiptCount: number;
  receiptCap: number;
  receiptsRemaining: number;
  receiptOccupancy: number;
  level: 'ok' | 'warning' | 'critical' | 'exhausted';
  /** `failed` and `lastFailure` are absent on a service older than this build. */
  queue: { depth: number; cap: number; dropped: number; failed?: number; lastFailure?: string; batchesCommitted: number; rowsCommitted: number; batchMax: number };
  backgroundFault?: string;
}

/**
 * Lifecycle evidence that keeps failing to reach the ledger. `stuck` events are retried on a backoff (1 minute
 * doubling to an hour); `quarantined` ("set aside") events failed deterministically, have a substitute ledger
 * row, and are retried every six hours. The optional fields are absent on a service older than this build.
 */
export interface LifecycleFaultSummary {
  stuck: number;
  quarantined: number;
  faults: Array<{
    actionId: string;
    eventId: string;
    kind: string;
    app: string;
    attempts: number;
    deterministicAttempts?: number;
    firstFailedAt: number;
    lastFailedAt: number;
    lastError: string;
    lastClass?: 'deterministic' | 'retryable';
    nextAttemptAt?: number;
    quarantinedAt?: number;
  }>;
  storeFault?: string;
}

/** Pure view model for the capacity badge, so the alert thresholds are testable without a DOM. */
export function auditCapacityView(capacity: AuditCapacity): { tone: 'ok' | 'warn' | 'bad'; percent: number; label: string; alert: boolean } {
  const percent = Math.min(100, Math.round(capacity.receiptOccupancy * 1000) / 10);
  const tone = capacity.level === 'ok' ? 'ok' : capacity.level === 'warning' ? 'warn' : 'bad';
  const label = capacity.level === 'exhausted'
    ? 'receipt capacity exhausted'
    : `receipts ${percent}% used`;
  return { tone, percent, label, alert: capacity.level !== 'ok' };
}
